import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Head } from 'common/components/Head';
import { Meta } from 'types/meta';
import ReactPixel from 'react-facebook-pixel';
import { Framework } from 'client/components/Framework';
import { getTranslation } from 'localization/helpers';
import { CompanyName } from 'styles/CompanyName';
import reviewIcon from 'images/reviewIcon.svg';
import arrowIcon from 'images/arrowIcon.svg';
import { Address } from 'styles/Address';
import { Place } from 'types/place';
import { Pad } from 'styles/Pad';
import { MenuBlock } from 'client/components/MenuBlock';
import { NavigationButton } from 'client/components/NavigationButtons/NavigationButton';
import { renderButton } from 'client/components/NavigationButtons/LinkButton';
import { QrType } from 'types/qrType';
import { SocialNetworks } from 'client/components/SocialNetworks';
import { StyledLink } from 'styles/Form/StyledLink';

export const PlaceMainPage: FC<{
  place: Place;
  meta: Meta;
  qrType: QrType;
  hash: string;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
}> = ({ place, meta, qrType, hash }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = place.location;
  const company = place.company;

  useEffect(() => {
    let redirect = true;
    if (
      place.menu.length > 0 ||
      place.actions.length > 0 ||
      location.social_networks.length > 0 ||
      location.tip_available
    )
      redirect = false;
    if (redirect) navigate('feedback');
    ReactPixel.init(company.facebook_pixel);
    ReactPixel.pageView();
  }, []);

  const companyName = getTranslation(
    company.translations,
    'title',
    i18n.language,
    meta.default_locale
  );

  const address = getTranslation(
    location.translations,
    'title',
    i18n.language,
    meta.default_locale
  );

  const notification = getTranslation(
    company.translations,
    'notification',
    i18n.language,
    meta.default_locale
  );
  const metaTitle = `${t('mainPage')} – ${companyName}`;

  return (
    <Framework locales={meta.locales} logo={meta.logo} color={meta.color}>
      <Head title={metaTitle} description={metaTitle} />
      {companyName && <CompanyName>{companyName}</CompanyName>}
      <Address>{address}</Address>
      <MenuBlock menus={place.menu} meta={meta} />
      <StyledLink to={`feedback`}>
        <NavigationButton
          text={t('giveFeedback')}
          color={meta.color}
          icon={reviewIcon}
          arrow={arrowIcon}
        />
      </StyledLink>
      {place.actions.map((button, index) =>
        renderButton(button, index, meta, {
          act_id: button.id.toString(),
          place_hash: hash
        })
      )}
      {location.social_networks.length > 0 && (
        <SocialNetworks
          networks={location.social_networks}
          color={meta.color}
        />
      )}
      {notification && <Pad>{notification}</Pad>}
    </Framework>
  );
};
